import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { eventBus } from '@/events/eventBus';
import { seedNow } from '@/domain/seedContext';
import type { NoRebookFlag } from '@/domain/types';

function nowIso(): string {
  return seedNow() ?? useSimulationStore.getState().clockIso;
}

/**
 * Leg (a) closure for a no-rebook mid-piece flag (diagrams 01/09). Once Ops has
 * seen it and actioned the follow-up, stamp `resolvedAt` so it drops out of the
 * worklist (`buildOpsWorklist` filters `!f.resolvedAt`). There is no 'finished'
 * button on the piece itself - resolving only clears the Ops accountability flag;
 * the Sweeper's open-business re-call (leg b) is emergent and unaffected. No-op
 * when the flag is missing or already resolved.
 */
export function resolveNoRebookFlag(
  flagId: string,
  opts: { by?: string } = {},
): NoRebookFlag | undefined {
  const brain = useBrainStore.getState();
  const flag = brain.noRebookFlags[flagId];
  if (!flag || flag.resolvedAt) return undefined;

  const at = nowIso();
  const resolved: NoRebookFlag = { ...flag, resolvedAt: at };
  brain.upsertNoRebookFlag(resolved);

  brain.appendActivity({
    id: `act-norebook-resolved-${flag.id}`,
    type: 'rebook-declined',
    customerId: flag.customerId,
    studioId: flag.studioId,
    body: `No-rebook flag cleared by ${opts.by ?? 'Ops'}.`,
    timestamp: at,
  });

  eventBus.publish('no-rebook:resolved', {
    flagId: flag.id,
    customerId: flag.customerId,
    pieceId: flag.pieceId,
  });

  return resolved;
}
